import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter } from 'lucide-react';
import { ToolCard } from './ToolCard';
import { CategoryNav } from './CategoryNav';
import { Tool } from '../types/database';
interface NewToolsSectionProps {
  tools: Tool[];
  loading: boolean;
  activeCategory: string;
  setActiveCategory: (category: string) => void;
  onFilterClick?: () => void;
  activeFilterCount?: number;
  selectedCategories?: string[];
}
export function NewToolsSection({
  tools,
  loading,
  activeCategory,
  setActiveCategory,
  onFilterClick,
  activeFilterCount = 0,
  selectedCategories = []
}: NewToolsSectionProps) {
  // Additional category filtering on top of sidebar filters
  const toolBelongsToCategory = (tool: Tool, category: string): boolean => {
    if (category === 'all') return true;
    // Check new categories array first (multi-category support)
    if (tool.categories && tool.categories.length > 0) {
      return tool.categories.includes(category);
    }
    // Fallback to legacy single category field
    return tool.category === category;
  };
  const filteredTools = activeCategory === 'all' ? tools : tools.filter(tool => toolBelongsToCategory(tool, activeCategory));
  return <section className="py-16 px-4 max-w-7xl mx-auto" data-id="element-400">
      <div className="flex flex-col md:flex-row md:items-end justify-between md:mb-8 gap-4" data-id="element-401">
        <div className="mb-0 md:mb-0" data-id="element-402">
          <h2 className="text-3xl md:text-4xl text-white mb-3 font-subtitle" data-id="element-403">
            More AI Tools
          </h2>
          <p className="text-gray-400 text-sm max-w-xl" data-id="element-404">
            Discover the latest AI innovations across every category. Updated
            daily.
          </p>
        </div>

        {/* Filter Button - 26px margin-top on mobile */}
        {onFilterClick && <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={onFilterClick} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500/20 to-purple-500/20 hover:from-cyan-500/30 hover:to-purple-500/30 border border-cyan-500/30 text-white font-medium transition-all relative mt-[26px] md:mt-0" data-id="element-405">
            <Filter size={18} data-id="element-406" />
            <span data-id="element-407">Filters</span>
            {activeFilterCount > 0 && <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-xs font-bold" data-id="element-408">
                {activeFilterCount}
              </motion.div>}
          </motion.button>}
      </div>

      {/* Category Nav - reduced top margin on mobile (15px less = ~1rem) */}
      <div className="mt-1 md:mt-0" data-id="element-409">
        <CategoryNav activeCategory={activeCategory} setActiveCategory={setActiveCategory} selectedCategories={selectedCategories} data-id="element-410" />
      </div>

      {loading ? <div className="text-center py-20" data-id="element-411">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent" data-id="element-412"></div>
          <p className="text-gray-400 mt-4" data-id="element-413">Loading tools...</p>
        </div> : <AnimatePresence mode="wait" data-id="element-414">
          <motion.div key={`${activeCategory}-${filteredTools.length}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8" data-id="element-415">
            {filteredTools.map((tool, index) => <motion.div key={tool.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.4,
          delay: index * 0.03
        }} className="h-full" data-id="element-416">
                <ToolCard tool={tool} data-id="element-417" />
              </motion.div>)}
          </motion.div>
        </AnimatePresence>}

      {!loading && filteredTools.length === 0 && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center py-20" data-id="element-418">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/5 border border-white/10 mb-6" data-id="element-419">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-500" data-id="element-420">
              <circle cx="11" cy="11" r="8" data-id="element-421" />
              <path d="m21 21-4.35-4.35" data-id="element-422" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-423">No tools found</h3>
          <p className="text-gray-400" data-id="element-424">
            Try adjusting your filters or search criteria
          </p>
        </motion.div>}
    </section>;
}